'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

export default function HeroSection() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    // Generate procedural bar chart
    if (canvasRef?.current) {
      const container = canvasRef?.current;
      const totalBars = 90;

      for (let i = 0; i < totalBars; i++) {
        const bar = document.createElement('div');
        bar.className = 'shrink-0 rounded-sm transition-all duration-300';
        bar.style.width = '4px';

        let h = 40;
        let color = '#111827'; // gray-900

        // Segment 1: Left arch (0-28) - Cooling (Blue)
        if (i < 28) {
          h = 65 + Math.sin((i / 20) * Math.PI) * 35;
          if (i > 22) {
            color = '#2563EB'; // primary blue
            h = 95;
          }
        }
        // Segment 2: Middle gradient (28-60) - Transition
        else if (i < 60) {
          h = 100;
          if (i < 38) color = '#3B82F6'; // blue-500
          else if (i < 48) color = '#F59E0B'; // amber-500
          else if (i < 55) color = '#F97316'; // orange-500
          else color = '#EA580C'; // orange-600
        }
        // Segment 3: Right arch (60-90) - Heating (Orange)
        else {
          const relI = i - 60;
          h = 60 + Math.sin((relI / 30) * Math.PI) * 40;
          if (relI < 8) {
            color = '#F97316'; // secondary orange
          } else {
            color = '#111827'; // gray-900
          }
        }

        bar.style.height = `${h}%`;
        bar.style.backgroundColor = color;
        bar.style.animationDelay = `${i * 0.01}s`;
        bar?.classList?.add('animate-bar-grow');

        container?.appendChild(bar);
      }
    }
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-background to-muted/30">
      <div className="container-custom py-12 sm:py-16 md:py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div
            className={`space-y-6 md:space-y-8 ${
              isVisible ? 'animate-fade-in-blur' : 'opacity-0'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-primary/10 border border-primary/20 rounded-full">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse-glow" />
              <span className="text-xs uppercase tracking-widest font-semibold text-primary">
                Live Market Data
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold tracking-tight leading-[1.1]">
              <span className="text-foreground">Market-Driven</span>
              <br />
              <span className="text-gradient">HVAC Quotes</span>
              <br />
              <span className="text-foreground">in Seconds</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-xl">
              Stop guessing. Start quoting with confidence using real-time
              market pricing and local area intelligence.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link
                href="/free-trial"
                className="px-6 md:px-8 py-3 md:py-4 bg-primary text-primary-foreground rounded-full text-sm md:text-base font-semibold uppercase tracking-wide hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-lg text-center"
              >
                Start $1 Trial
              </Link>
              <Link
                href="#demo"
                className="px-6 md:px-8 py-3 md:py-4 bg-white border-2 border-border text-foreground rounded-full text-sm md:text-base font-semibold uppercase tracking-wide hover:border-primary hover:text-primary transition-all text-center"
              >
                Watch Demo
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 pt-4">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 text-success flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-xs sm:text-sm text-muted-foreground">
                  Payment method required
                </span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 text-success flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-xs sm:text-sm text-muted-foreground">
                  Free 14-day trial
                </span>
              </div>
            </div>
          </div>

          {/* Right Visual - Procedural Bar Chart */}
          <div
            className={`${isVisible ? 'animate-fade-in-blur delay-300' : 'opacity-0'}`}
          >
            <div className="relative">
              {/* Bar Chart Container */}
              <div
                ref={canvasRef}
                className="w-full h-48 sm:h-56 md:h-64 lg:h-80 flex items-center gap-[3px] md:gap-[4px] bg-white border border-border rounded-xl p-4 sm:p-6 md:p-8 shadow-2xl"
              />

              {/* Floating Quote Card */}
              <div className="absolute -bottom-4 sm:-bottom-6 -right-2 sm:-right-6 bg-card border border-border rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-xl w-[calc(100%-2rem)] sm:w-auto sm:max-w-xs">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                    Live Calculation
                  </span>
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse-glow" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      Parts:
                    </span>
                    <span className="text-base sm:text-lg font-mono-numbers font-semibold text-foreground">
                      $1,247
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      Labor:
                    </span>
                    <span className="text-base sm:text-lg font-mono-numbers font-semibold text-foreground">
                      $580
                    </span>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs sm:text-sm font-semibold text-foreground">
                        Total:
                      </span>
                      <span className="text-xl sm:text-2xl font-mono-numbers font-bold text-primary">
                        $1,827
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative blur */}
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 md:w-96 md:h-96 bg-primary/10 blur-[120px] rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}